import { type } from 'arktype';

/**
 * External billing rate hierarchy (spec: faktura.md, Story 1/2).
 *
 * A `CostRate` row is one of exactly three shapes, distinguished by which of
 * `projectId`/`activityTypeId` are set:
 * - both `null` — the single global default (DB-enforced: at most one row),
 * - `projectId` set, `activityTypeId` `null` — a project default,
 * - both set — a project × activity-type override.
 *
 * The scope is derived client-side from the two ids; the API does not
 * persist it separately.
 */
export type CostRateScope = 'global' | 'project' | 'override';

export interface CostRate {
  id: string;
  projectId: string | null;
  activityTypeId: string | null;
  /** CHF per hour, at most two decimal places. */
  rate: number;
  createdAt: string;
  updatedAt: string;
}

/** Derives the scope of a cost rate from its ids (mirrors the DB's partial unique indexes). */
export function costRateScope(costRate: Pick<CostRate, 'projectId' | 'activityTypeId'>): CostRateScope {
  if (costRate.projectId === null) return 'global';
  return costRate.activityTypeId === null ? 'project' : 'override';
}

/**
 * Bounds mirror the `numeric(10, 2)` column: at most two decimal places and
 * a non-negative value — anything else would surface as a Postgres
 * check-constraint violation (500) instead of a friendly 400.
 */
const costRateValueSchema = type('0 <= number <= 999999.99').narrow(
  (value, ctx) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-9 || ctx.mustBe('a number with at most two decimal places')
);

const projectIdSchema = type('string.uuid').configure({ description: 'Id of the project this rate applies to.' });
const activityTypeIdSchema = type('string.uuid').configure({ description: 'Id of the activity type this override applies to.' });

export const setGlobalCostRateSchema = type({
  rate: costRateValueSchema.configure({
    description: 'Global default external rate (CHF/hour), used when neither a project default nor an override applies.',
    examples: [120],
  }),
}).configure({ description: 'Upserts the single global default cost rate.' });
export type SetGlobalCostRate = typeof setGlobalCostRateSchema.t;

export const createCostRateSchema = type({
  projectId: projectIdSchema,
  'activityTypeId?': activityTypeIdSchema.or('null').configure({
    description: 'Omit or `null` for a project default; set for a project × activity-type override.',
  }),
  rate: costRateValueSchema.configure({
    description: 'External rate (CHF/hour) for this project (default) or project × activity type (override).',
    examples: [135],
  }),
}).configure({ description: 'Creates a project default rate (activityTypeId omitted) or an override (activityTypeId set).' });
export type CreateCostRate = typeof createCostRateSchema.t;

export const updateCostRateSchema = type({
  rate: costRateValueSchema.configure({ description: 'Updated rate (CHF/hour).' }),
}).configure({ description: 'Updates the rate of a project default or override. The scope (project/activity type) is immutable.' });
export type UpdateCostRate = typeof updateCostRateSchema.t;

/** Resolved rate for a (project, activity type) pair, following the hierarchy override > project default > global default. */
export interface EffectiveCostRate {
  rate: number;
  source: CostRateScope;
  costRateId: string;
}
